import { LoadingOutlined, SyncOutlined } from "@ant-design/icons";
import { Button, Spin, Tooltip, message } from "antd";
import { useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { useGetProjectINTGState } from "hooks/submittal";
import { invokeACCProjectSyncJob } from "services/integration-service";
import { markOpcSyncStarted } from "services/opc-sync-state";
import { isGcProjectAdminRole, OPC_INTEGRATION_SYSTEM } from "constants/index";
import useTrackedOpcSync from "./use-opc-sync";
import { OPC_SYNC_FAILURE_MESSAGE } from "./opc-sync-watcher";

// Lives in constants/index.ts now that the integrations grid needs the same
// string to name and render OPC rows. Re-exported because this module was its
// original home.
export { OPC_INTEGRATION_SYSTEM };

const SYNC_TOOLTIP = "Sync schedules";
const SYNC_IN_PROGRESS_TOOLTIP = "Sync is in progress";
const NOT_CONFIGURED_TOOLTIP =
  "OPC integration is not configured for this project";

/**
 * Sync control for the "Last synced" line, mirroring the submittal log's
 * integration-mode sync button: same antd small icon button, same swap to a
 * spinner for as long as the job runs, same "Sync Started" toast, and the same
 * gc_project_admin gate — invoking a project-wide sync is an admin action there
 * and stays one here.
 *
 * The spinner spans the whole job, not just the POST: the id the POST returns is
 * handed to the OPC sync singleton, which OpcSyncWatcher polls to a
 * terminal status. That also means a second sync cannot be fired on top of a
 * running one, and the running state survives navigating away and back.
 */
function SchedulesInOpcSyncButton() {
  const {
    gqlClientForProject,
    tokenContents,
    tokenRetrievalState,
    isProjectArchived
  }: TProjectContext = useContext(ProjectContext);

  const { projectId } = useParams<{ projectId: string }>();

  // Covers the POST itself. Once it resolves, the singleton takes over.
  const [invokingSync, setInvokingSync] = useState(false);

  const trackedSync = useTrackedOpcSync();
  const isSyncRunning = invokingSync || trackedSync?.projectId === projectId;

  const isLoggedInUserGcAdmin = isGcProjectAdminRole(tokenContents?.role);

  // Read straight from project_integration_configs rather than from
  // ProjectContext.projectIntegrationConfig: the context copy is only fetched for
  // projects in submittal integration mode, which an OPC project need not be.
  const { data: projectIntegrationData } = useGetProjectINTGState({
    client: gqlClientForProject,
    skip: !gqlClientForProject || !isLoggedInUserGcAdmin
  });

  const opcProjectConfigId: string | null = useMemo(() => {
    const configs = projectIntegrationData?.project_integration_configs ?? [];
    const opcConfig = configs.find(
      (config: any) =>
        config?.system_integration_config?.integration?.system ===
        OPC_INTEGRATION_SYSTEM
    );
    return opcConfig?.id ?? null;
  }, [projectIntegrationData]);

  const isDisabled =
    isSyncRunning || !opcProjectConfigId || !!isProjectArchived;

  const tooltip = useMemo(() => {
    if (isProjectArchived) return undefined;
    if (isSyncRunning) return SYNC_IN_PROGRESS_TOOLTIP;
    if (!opcProjectConfigId) return NOT_CONFIGURED_TOOLTIP;
    return SYNC_TOOLTIP;
  }, [isProjectArchived, isSyncRunning, opcProjectConfigId]);

  const syncData = async () => {
    setInvokingSync(true);
    const invokeSyncResponse = await invokeACCProjectSyncJob(
      {
        project_config_id: opcProjectConfigId as string,
        // The backend otherwise honours the integration's own sync interval; the
        // button is an explicit "sync now", so it asks for the job outright.
        force_sync: true
      },
      tokenRetrievalState?.token,
      // Reported here instead, so a rejected POST reads the same as a job that
      // starts and then fails. ErrorManager would otherwise toast its generic
      // "Something has failed. Try again later." — it never shows the server's
      // own message, only that one.
      false
    );
    // The id names the run just started; handing it over is what lets the
    // watcher poll THIS job rather than the latest OPC import, and what keeps
    // the button spinning until it lands. Without an id there is nothing to
    // poll, so the control simply falls back to releasing on the POST.
    const importLogId = invokeSyncResponse?.data?.import_log_id;
    if (invokeSyncResponse.success && importLogId) {
      markOpcSyncStarted({ importLogId, projectId, startedAt: Date.now() });
    }
    if (invokeSyncResponse.success) {
      message.info("Sync Started");
    } else {
      // Nothing was tracked, so OpcSyncWatcher will never run and never report
      // this. The sync failed before it began; same wording either way.
      message.error(OPC_SYNC_FAILURE_MESSAGE);
    }
    // On success the singleton now owns the running state, so dropping this
    // local flag does not release the button.
    setInvokingSync(false);
  };

  if (!isLoggedInUserGcAdmin) return null;

  return (
    <div className="shrink-0" data-testid="schedules-in-opc-sync">
      <Tooltip title={tooltip} destroyOnHidden placement="right">
        {/* antd needs a non-disabled wrapper to hover against: a disabled button
            emits no mouse events, so without this the reason it is disabled would
            be the one tooltip a user can never read. */}
        <span className="inline-flex">
          <Button
            disabled={isDisabled}
            size="small"
            icon={
              isSyncRunning ? (
                <Spin
                  indicator={
                    <LoadingOutlined style={{ color: "var(--ciq-fg)" }} spin />
                  }
                  size="small"
                />
              ) : (
                <SyncOutlined />
              )
            }
            onClick={syncData}
            data-testid="schedules-in-opc-sync-btn"
          />
        </span>
      </Tooltip>
    </div>
  );
}

export default SchedulesInOpcSyncButton;
